import React, {
  useState, useCallback, useRef, useMemo, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Row } from 'antd';
import { DateTime } from 'luxon';

import CustomModal from '../../common/modals/CustomModal';
import colors from '../../constants/Colors';
import ScheduleDay from '../ScheduleDay';
import DailyScheduleHeader from '../DailySchedule/DailyScheduleHeader';
import { HOUR_HEIGHT } from '../schedule.constants';

import { splitMultiDayShifts, getDayKey } from '../scheduleHelpers';
import { isShiftInRow } from './resourceRowLayout';
import { resourceAxisPropType, resourceRowPropType } from './resourceAxis.propTypes';

export default function ResourceDayModal({
  axis,
  rows,
  onCloseClicked,
  onShiftSelect,
  onShiftEdit,
  editShift,
  onShiftCreate,
  newShift,
  onNewShiftChange,
  viewType,
}) {
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);

  const {
    rowId,
    date,
    visible,
  } = useSelector((state) => state.schedule.biweeklyModalConfig);
  const shifts = useSelector((state) => state.schedule.shifts);
  const { shiftField } = axis;

  const selectedRow = useMemo(() => (
    rowId ? rows.find((row) => row?.id === rowId) : undefined
  ), [rows, rowId]);

  const getRowShiftDefaults = useCallback(() => {
    const [divisionId = null] = selectedRow?.divisionIds ?? [];
    return {
      ...axis.getAssignment(selectedRow),
      divisionId,
    };
  }, [axis, selectedRow]);

  const {
    multiDay: multiDayShifts = [],
    interDay: interDayShifts = [],
  } = useMemo(() => {
    if (!visible || !date) return {};
    const allShiftsForDay = shifts[getDayKey(date)] ?? [];
    const rowShifts = allShiftsForDay.filter((shift) => isShiftInRow(shift, rowId, shiftField));
    return splitMultiDayShifts(rowShifts, date);
  }, [shifts, date, rowId, shiftField, visible]);

  const isDraftInRow = useCallback((shift) => {
    if (!shift) return false;
    const { [shiftField]: shiftRowIds = [] } = shift;
    return rowId
      ? shiftRowIds.includes(rowId)
      : shiftRowIds.every((id) => !id);
  }, [shiftField, rowId]);

  const onScroll = useCallback((e) => {
    const {
      target: {
        scrollTop: targetTop,
      } = {},
    } = e;
    setScrollTop(targetTop);
  }, []);

  useEffect(() => {
    if (!visible || !scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: HOUR_HEIGHT * 7,
    });
  }, [visible, date]);

  const onNewRowShift = useCallback((rowShift) => {
    const { divisionId, ...assignment } = getRowShiftDefaults();
    const fullShift = { ...rowShift, ...assignment };
    if (divisionId) fullShift.divisionId = divisionId;
    onNewShiftChange(fullShift);
  }, [getRowShiftDefaults, onNewShiftChange]);

  const onAllDayClick = useCallback((e) => {
    e.stopPropagation();
    if (!date) return;
    const startTime = date.startOf('day').toMillis();
    const endTime = date.endOf('day').toMillis();
    const allDayShift = {
      id: DateTime.local().toMillis(),
      day: date,
      startTime,
      endTime,
      ...getRowShiftDefaults(),
    };
    onShiftCreate(allDayShift);
    onNewShiftChange(allDayShift);
  }, [date, getRowShiftDefaults, onShiftCreate, onNewShiftChange]);

  return (
    <CustomModal
      title="Select a Time Block"
      visible={visible}
      onCloseClicked={onCloseClicked}
      maskColor={colors.MODAL_MASK_DARK}
      destoryOnClose
      width={600}
      zIndex={15}
    >
      <Row
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: -20,
          marginBottom: 8,
        }}
      >
        {selectedRow?.name || axis.unassignedLabel}
      </Row>
      {!!date && (
        <div className="schedule-daily-view" style={{ height: '70vh' }}>
          <div className="schedule-daily-title">{date.toFormat('ccc, LLLL d')}</div>
          <DailyScheduleHeader
            onClick={onAllDayClick}
            shifts={multiDayShifts}
            onShiftSelect={onShiftSelect}
            onShiftEdit={onShiftEdit}
            card
            showEmptyLabel
          />
          <div
            className="schedule-view-container"
            onScroll={onScroll}
            ref={scrollRef}
          >
            <ScheduleDay
              key="daily"
              day={date}
              shifts={interDayShifts}
              onShiftCreate={onShiftCreate}
              newShift={isDraftInRow(newShift) ? newShift : null}
              onNewShiftChange={onNewRowShift}
              onShiftSelect={onShiftSelect}
              editShift={isDraftInRow(editShift) ? editShift : null}
              onShiftEdit={onShiftEdit}
              scrollTop={scrollTop}
              viewType={viewType}
              card
            />
          </div>
        </div>
      )}
    </CustomModal>
  );
}

/* eslint-disable react/forbid-prop-types */
ResourceDayModal.propTypes = {
  axis: resourceAxisPropType.isRequired,
  rows: PropTypes.arrayOf(resourceRowPropType).isRequired,
  onCloseClicked: PropTypes.func.isRequired,
  onShiftSelect: PropTypes.func.isRequired,
  onShiftEdit: PropTypes.func.isRequired,
  editShift: PropTypes.object,
  onShiftCreate: PropTypes.func.isRequired,
  newShift: PropTypes.object,
  onNewShiftChange: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
};

ResourceDayModal.defaultProps = {
  editShift: undefined,
  newShift: undefined,
};
